import { useId } from '@finn-no/troika-react-utils';
import classNames from 'classnames';
import React, { forwardRef, useContext } from 'react';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var idPrefix = function idPrefix(id) {
  return "ads__unit__content__title" + id;
};

var AriaOwnsContext = /*#__PURE__*/React.createContext(0);
var HeadingLevelContext = /*#__PURE__*/React.createContext(2);
var Ads = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var children = _ref.children,
      className = _ref.className,
      _ref$headingLevel = _ref.headingLevel,
      headingLevel = _ref$headingLevel === void 0 ? 2 : _ref$headingLevel,
      liquid = _ref.liquid,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "className", "headingLevel", "liquid"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    ref: ref,
    className: classNames('ads', className, {
      'ads--liquid': liquid,
      'ads--liquid--cols1to2': liquid === '1to2',
      'ads--liquid--cols2to3': liquid === '2to3',
      'ads--liquid--cols1to3': liquid === '1to3'
    })
  }), /*#__PURE__*/React.createElement(HeadingLevelContext.Provider, {
    value: headingLevel
  }, children));
});
var Ad = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
      className = _ref2.className,
      ariaOwns = _ref2['aria-owns'],
      props = _objectWithoutPropertiesLoose(_ref2, ["children", "className", "aria-owns"]);

  var id = useId(ariaOwns);
  return /*#__PURE__*/React.createElement("article", _extends({}, props, {
    className: classNames('ads__unit', className),
    ref: ref
  }), /*#__PURE__*/React.createElement(AriaOwnsContext.Provider, {
    value: id
  }, id && /*#__PURE__*/React.createElement("div", {
    "aria-owns": idPrefix(id)
  }), children));
});
var AdImage = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
      className = _ref3.className,
      props = _objectWithoutPropertiesLoose(_ref3, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__img', className),
    ref: ref
  }), children);
});
var AdImageFrame = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var children = _ref4.children,
      className = _ref4.className,
      props = _objectWithoutPropertiesLoose(_ref4, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__img__frame', className),
    ref: ref
  }), children);
});
var AdImageFrameOverlay = /*#__PURE__*/forwardRef(function (_ref5, ref) {
  var children = _ref5.children,
      className = _ref5.className,
      props = _objectWithoutPropertiesLoose(_ref5, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__img__frame__overlay', className),
    ref: ref
  }), children);
});
var AdContent = /*#__PURE__*/forwardRef(function (_ref6, ref) {
  var children = _ref6.children,
      className = _ref6.className,
      props = _objectWithoutPropertiesLoose(_ref6, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__content', className),
    ref: ref
  }), children);
});
var AdContentStatus = /*#__PURE__*/forwardRef(function (_ref7, ref) {
  var children = _ref7.children,
      className = _ref7.className,
      props = _objectWithoutPropertiesLoose(_ref7, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__content__status', className),
    ref: ref
  }), children);
});
var AdContentDetails = /*#__PURE__*/forwardRef(function (_ref8, ref) {
  var children = _ref8.children,
      className = _ref8.className,
      props = _objectWithoutPropertiesLoose(_ref8, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__content__details', className),
    ref: ref
  }), children);
});
var AdContentTitle = /*#__PURE__*/forwardRef(function (_ref9, ref) {
  var children = _ref9.children,
      className = _ref9.className,
      _ref9$lineClamp = _ref9.lineClamp2,
      lineClamp2 = _ref9$lineClamp === void 0 ? false : _ref9$lineClamp,
      props = _objectWithoutPropertiesLoose(_ref9, ["children", "className", "lineClamp2"]);

  var id = useContext(AriaOwnsContext);
  var headingLevel = useContext(HeadingLevelContext);
  var validHeadingLevel = Math.max(1, Math.min(6, headingLevel));
  var Heading = "h" + validHeadingLevel;
  return /*#__PURE__*/React.createElement(Heading, _extends({}, props, {
    className: classNames('ads__unit__content__title', className, {
      'ads__unit__content__title--line-clamp2': lineClamp2
    }),
    id: id ? idPrefix(id) : undefined,
    ref: ref
  }), children);
});
var AdLink = /*#__PURE__*/forwardRef(function (_ref10, ref) {
  var children = _ref10.children,
      className = _ref10.className,
      props = _objectWithoutPropertiesLoose(_ref10, ["children", "className"]);

  return /*#__PURE__*/React.createElement("a", _extends({}, props, {
    className: classNames('ads__unit__link', className),
    ref: ref
  }), children);
});
var AdTools = /*#__PURE__*/forwardRef(function (_ref11, ref) {
  var children = _ref11.children,
      className = _ref11.className,
      props = _objectWithoutPropertiesLoose(_ref11, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__tools', className),
    ref: ref
  }), children);
});
var AdBadge = /*#__PURE__*/forwardRef(function (_ref12, ref) {
  var children = _ref12.children,
      className = _ref12.className,
      props = _objectWithoutPropertiesLoose(_ref12, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__badge', className),
    ref: ref
  }), children);
});
var AdLogo = /*#__PURE__*/forwardRef(function (_ref13, ref) {
  var children = _ref13.children,
      className = _ref13.className,
      props = _objectWithoutPropertiesLoose(_ref13, ["children", "className"]);

  return /*#__PURE__*/React.createElement("div", _extends({}, props, {
    className: classNames('ads__unit__logo', className),
    ref: ref
  }), children);
});
Ads.displayName = "Ads";
Ad.displayName = "Ad";
AdImage.displayName = "AdImage";
AdImageFrame.displayName = "AdImageFrame";
AdImageFrameOverlay.displayName = "AdImageFrameOverlay";
AdContent.displayName = "AdContent";
AdContentStatus.displayName = "AdContentStatus";
AdContentDetails.displayName = "AdContentDetails";
AdContentTitle.displayName = "AdContentTitle";
AdLink.displayName = "AdLink";
AdTools.displayName = "AdTools";
AdBadge.displayName = "AdBadge";
AdLogo.displayName = "AdLogo";

export { Ad, AdBadge, AdContent, AdContentDetails, AdContentStatus, AdContentTitle, AdImage, AdImageFrame, AdImageFrameOverlay, AdLink, AdLogo, AdTools, Ads };
//# sourceMappingURL=index.es.js.map
